import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaMousePointer,
  FaUsers,
  FaCheckCircle,
  FaExclamationCircle,
  FaCamera,
  FaArrowsAltV,
  FaHandPointer,
  FaEye,
  FaInfoCircle,
  FaLayerGroup,
  FaCalendarAlt,
  FaChartLine,
  FaFileAlt
} from 'react-icons/fa';
import HeatmapCanvas from '../charts/HeatmapCanvas';
import DailyTrendChart from '../charts/DailyTrendChart';
import { getPageSnapshot } from '../../../../services/heatmap.service';
import './HeatmapViewerTab.css';

const snapshotMemoryCache = new Map();

export function getCachedSnapshot(snapCacheKey) {
  if (!snapCacheKey) return null;
  if (snapshotMemoryCache.has(snapCacheKey)) {
    return snapshotMemoryCache.get(snapCacheKey);
  }
  if (typeof window !== 'undefined') {
    try {
      const cached = window.sessionStorage.getItem(snapCacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && (parsed.htmlSnapshot || parsed.url)) {
          snapshotMemoryCache.set(snapCacheKey, parsed);
          return parsed;
        }
      }
    } catch (_) {}
  }
  return null;
}

/**
 * Tab 2: Interaction Density & Heatmap Analytics Viewer.
 * Visualizes:
 * 1. Control Bar: Page Dropdown, Layout Version, Date Range [From, To), Mode Switcher
 * 2. KPI Summary Banner: Total Interactions, Unique Sessions, Daily Trend, Capture & DOM Status
 * 3. Main Heatmap Canvas (Stacked Viewport):
 *    - Layer 0 (Bottom): Sandboxed <iframe> rendering DOM Snapshot (srcDoc, sandbox="allow-same-origin")
 *    - Layer 1 (Top): Transparent <canvas> rendering Color Heatmap Gradients & Overlays
 * 4. Mode-specific Analytics:
 *    - Mode A: Click Heatmap (Density) + DailyTrendChart + sliders
 *    - Mode B: Scroll Reach (Drop-off) + Depth Milestone Curve + Right margin drop-off badges
 *    - Mode C: Hover Dwell (Mouse engagement) + Top 10 ranking table + Touch exclusion notice
 *    - Mode D: Section Attention (Section visibility) + Bar chart of seconds spent + Disclaimer
 */
export default function HeatmapViewerTab({
  summary,
  daily = [],
  density = [],
  scrollReach = null,
  hoverDwell = null,
  attention = null,
  pages = [],
  selectedPageId = '',
  selectedLayoutVersion = null,
  onPageChange,
  onLayoutVersionChange,
  fromDate = '',
  toDate = '',
  organizationId = '',
  siteCollectionId = '',
  hasSessionReplayFeature = false,
  isLoading = false,
  error = null,
  refreshNonce = 0
}) {
  const [activeSubView, setActiveSubView] = useState('density');
  const [internalLayoutVersion, setInternalLayoutVersion] = useState(1);

  const selectedPage = useMemo(() => {
    return pages.find((p) => p.pageId === selectedPageId) || null;
  }, [pages, selectedPageId]);

  const activeLayoutVersion = selectedLayoutVersion != null
    ? selectedLayoutVersion
    : (selectedPage?.layoutVersion || internalLayoutVersion || 1);

  const snapCacheKey = organizationId && siteCollectionId && selectedPageId
    ? `cognior_page_snap_${organizationId}_${siteCollectionId}_${selectedPageId}_v${activeLayoutVersion}`
    : null;

  const [snapshot, setSnapshot] = useState(() => getCachedSnapshot(snapCacheKey));

  const effectiveSnapshot = (snapshot?.pageId === selectedPageId || (!snapshot?.pageId && snapshot))
    ? snapshot
    : getCachedSnapshot(snapCacheKey);

  useEffect(() => {
    let isCurrent = true;
    if (!organizationId || !siteCollectionId || !selectedPageId) {
      setSnapshot(null);
      return;
    }

    const version = activeLayoutVersion;
    const cacheKey = `cognior_page_snap_${organizationId}_${siteCollectionId}_${selectedPageId}_v${version}`;

    // Fast restore from cache if state doesn't have it (e.g. page changed)
    const cached = getCachedSnapshot(cacheKey);
    if (cached) {
      setSnapshot(cached);
    }

    getPageSnapshot(organizationId, siteCollectionId, selectedPageId, version)
      .then((data) => {
        if (isCurrent) {
          if (data && (data.htmlSnapshot || data.url)) {
            const payload = { ...data, pageId: selectedPageId };
            snapshotMemoryCache.set(cacheKey, payload);
            setSnapshot(payload);
            if (typeof window !== 'undefined') {
              try {
                window.sessionStorage.setItem(cacheKey, JSON.stringify(payload));
              } catch (e) {}
            }
          } else {
            setSnapshot((prev) => (prev?.pageId === selectedPageId && (prev?.htmlSnapshot || prev?.url) ? prev : null));
          }
        }
      })
      .catch(() => {
        if (isCurrent) {
          setSnapshot((prev) => (prev?.pageId === selectedPageId && (prev?.htmlSnapshot || prev?.url) ? prev : null));
        }
      });

    return () => { isCurrent = false; };
  }, [organizationId, siteCollectionId, selectedPageId, activeLayoutVersion, refreshNonce]);

  const totalInteractions = summary?.totalInteractions || 0;
  const uniqueSessions = summary?.uniqueSessions || 0;
  const captureObserved = Boolean(summary?.captureObserved);

  // Normalize scroll reach depth distribution
  const scrollMilestones = useMemo(() => {
    if (!scrollReach) return [];
    if (Array.isArray(scrollReach.depthDistribution) && scrollReach.depthDistribution.length > 0) {
      return scrollReach.depthDistribution;
    }
    if (Array.isArray(scrollReach.reachPoints) && scrollReach.reachPoints.length > 0) {
      const eligible = scrollReach.eligibleVisits || 1;
      return scrollReach.reachPoints.map((pt) => ({
        depthPercent: pt.percentile ?? pt.depthPercent,
        reachedVisits: pt.sessionCount ?? pt.reachedVisits ?? 0,
        reachRate: pt.reachRate != null ? pt.reachRate : ((pt.sessionCount ?? 0) / eligible)
      }));
    }
    return [];
  }, [scrollReach]);

  // Sort and top 10 elements for hover dwell
  const top10HoverElements = useMemo(() => {
    if (!hoverDwell || !Array.isArray(hoverDwell.elements)) return [];
    return [...hoverDwell.elements]
      .sort((a, b) => (b.hoverCount || 0) - (a.hoverCount || 0))
      .slice(0, 10);
  }, [hoverDwell]);

  return (
    <div className="heatmap-viewer-tab" data-testid="heatmap-viewer-tab">
      {/* 1. Control Bar (Page Dropdown, Layout Version, Date Range, Mode Switcher) */}
      <div className="viewer-control-bar" data-testid="heatmap-control-bar">
        <div className="control-bar-selectors">
          {/* Page Selector */}
          <div className="control-field">
            <label htmlFor="viewer-page-selector">
              <FaFileAlt className="control-icon" />
              <span>Page:</span>
            </label>
            <select
              id="viewer-page-selector"
              data-testid="viewer-page-selector"
              value={selectedPageId}
              onChange={(e) => onPageChange && onPageChange(e.target.value)}
              disabled={!pages || pages.length === 0}
              className="control-select"
            >
              {!pages || pages.length === 0 ? (
                <option value="">No registered pages</option>
              ) : (
                pages.map((p) => (
                  <option key={p.pageId} value={p.pageId}>
                    {p.name || p.pageId}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Layout Version Selector */}
          <div className="control-field">
            <label htmlFor="viewer-layout-version">
              <FaLayerGroup className="control-icon" />
              <span>Layout Version:</span>
            </label>
            <select
              id="viewer-layout-version"
              data-testid="viewer-layout-version"
              value={activeLayoutVersion}
              onChange={(e) => {
                const v = Number(e.target.value);
                setInternalLayoutVersion(v);
                if (onLayoutVersionChange) onLayoutVersionChange(v);
              }}
              className="control-select"
            >
              {(() => {
                const maxVersion = Math.max(1, Number(selectedPage?.layoutVersion) || 1);
                return Array.from({ length: maxVersion }, (_, i) => i + 1).map((ver) => (
                  <option key={ver} value={ver}>
                    v{ver} {ver === maxVersion ? '(Active)' : '(Archived)'}
                  </option>
                ));
              })()}
            </select>
          </div>

          {/* Date Range Indicator */}
          {(fromDate || toDate) && (
            <div className="control-window-badge" title="Half-open query window [fromDate, toDate)">
              <FaCalendarAlt className="control-icon" />
              <span>{fromDate || 'Start'} &rarr; {toDate || 'End'}</span>
            </div>
          )}
        </div>

        {/* Mode Switcher Buttons */}
        <div className="viewer-subnav-bar" role="tablist" aria-label="Heatmap visualization modes">
          <button
            type="button"
            role="tab"
            aria-selected={activeSubView === 'density'}
            className={`subnav-pill ${activeSubView === 'density' ? 'active' : ''}`}
            onClick={() => setActiveSubView('density')}
            data-testid="subview-density-btn"
          >
            <FaMousePointer className="subnav-icon" /> Click Density
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeSubView === 'scroll'}
            className={`subnav-pill ${activeSubView === 'scroll' ? 'active' : ''}`}
            onClick={() => setActiveSubView('scroll')}
            data-testid="subview-scroll-btn"
          >
            <FaArrowsAltV className="subnav-icon" /> Scroll Reach
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeSubView === 'hover'}
            className={`subnav-pill ${activeSubView === 'hover' ? 'active' : ''}`}
            onClick={() => setActiveSubView('hover')}
            data-testid="subview-hover-btn"
          >
            <FaHandPointer className="subnav-icon" /> Element Hover Dwell
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeSubView === 'attention'}
            className={`subnav-pill ${activeSubView === 'attention' ? 'active' : ''}`}
            onClick={() => setActiveSubView('attention')}
            data-testid="subview-attention-btn"
          >
            <FaEye className="subnav-icon" /> Section Attention
          </button>
        </div>
      </div>

      {/* 2. KPI Summary Banner */}
      <div className="viewer-kpis-grid" data-testid="viewer-kpis-grid">
        <div className="viewer-kpi-card">
          <div className="kpi-icon-wrap blue">
            <FaMousePointer />
          </div>
          <div className="kpi-data">
            <span className="kpi-label">Total Interactions</span>
            <span className="kpi-value">{totalInteractions.toLocaleString()}</span>
          </div>
        </div>

        <div className="viewer-kpi-card">
          <div className="kpi-icon-wrap green">
            <FaUsers />
          </div>
          <div className="kpi-data">
            <span className="kpi-label">Unique Sessions</span>
            <span className="kpi-value">{uniqueSessions.toLocaleString()}</span>
          </div>
        </div>

        <div className="viewer-kpi-card" data-testid="daily-trend-kpi">
          <div className="kpi-icon-wrap purple">
            <FaChartLine />
          </div>
          <div className="kpi-data">
            <span className="kpi-label">Daily Trend</span>
            <span className="kpi-value">
              {daily && daily.length > 0
                ? `${daily[daily.length - 1]?.totalInteractions?.toLocaleString() || 0} latest`
                : 'Active Window'}
            </span>
          </div>
        </div>

        <div className="viewer-kpi-card">
          <div className={`kpi-icon-wrap ${captureObserved ? 'emerald' : 'amber'}`}>
            {captureObserved ? <FaCheckCircle /> : <FaExclamationCircle />}
          </div>
          <div className="kpi-data">
            <span className="kpi-label">Capture Status</span>
            <span className="kpi-value status-text">
              {captureObserved ? 'Active Capture' : 'No Traffic Observed'}
            </span>
          </div>
        </div>

        <div className="viewer-kpi-card" data-testid="snapshot-status-kpi">
          <div className={`kpi-icon-wrap ${effectiveSnapshot ? 'emerald' : 'amber'}`}>
            <FaCamera />
          </div>
          <div className="kpi-data">
            <span className="kpi-label">Mirrored DOM</span>
            <span className="kpi-value status-text">
              {effectiveSnapshot ? 'Captured & Mirrored' : 'Awaiting Snapshot'}
            </span>
          </div>
        </div>
      </div>

      {/* Error & Warning Banners */}
      {error && (
        <div className="viewer-error-banner" role="alert" data-testid="viewer-error-banner">
          {error}
        </div>
      )}

      {/* 3. Main Heatmap Canvas (Stacked Viewport) */}
      <div className="viewer-canvas-card" data-testid="click-density-view">
        <div className="canvas-header-row">
          <div>
            <h4 className="canvas-title">
              {activeSubView === 'density' && `Page Density Overlay: ${selectedPage?.name || 'Selected Page'}`}
              {activeSubView === 'scroll' && `Scroll Reach Map: ${selectedPage?.name || 'Selected Page'}`}
              {activeSubView === 'hover' && `Hover Dwell Map: ${selectedPage?.name || 'Selected Page'}`}
              {activeSubView === 'attention' && `Section Attention Map: ${selectedPage?.name || 'Selected Page'}`}
            </h4>
            <p className="canvas-sub">
              {activeSubView === 'density' && 'Aggregated click coordinates quantized to 2% screen-relative grid buckets.'}
              {activeSubView === 'scroll' && 'Vertical document penetration milestone gradient bands and visitor drop-off rates.'}
              {activeSubView === 'hover' && 'Interactive element hover frequencies and median engagement dwell duration.'}
              {activeSubView === 'attention' && 'Screen viewport dwell duration and visual exposure shading per page section.'}
            </p>
          </div>

        </div>

        {selectedPage && activeLayoutVersion < (Number(selectedPage.layoutVersion) || 1) && (
          <div
            className="heatmap-archived-banner"
            data-testid="heatmap-archived-banner"
            style={{
              background: '#fef3c7',
              border: '1px solid #fde68a',
              padding: '8px 16px',
              borderRadius: '6px',
              marginBottom: '12px',
              color: '#92400e',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaInfoCircle />
            <span>
              You are viewing historical layout <strong>v{activeLayoutVersion}</strong>. The active layout is <strong>v{selectedPage.layoutVersion}</strong>.
            </span>
          </div>
        )}

        <HeatmapCanvas
          density={density}
          snapshot={effectiveSnapshot}
          htmlSnapshot={effectiveSnapshot?.htmlSnapshot}
          mode={activeSubView}
          scrollReach={scrollReach}
          hoverDwell={hoverDwell}
          attention={attention}
          totalInteractions={totalInteractions}
          height={effectiveSnapshot ? 850 : 540}
          isLoading={isLoading}
        />
      </div>

      {/* 4. Visualization Details & Metrics Breakdown */}

      {/* Mode A: Daily Trend Chart */}
      {activeSubView === 'density' && (
        <DailyTrendChart daily={daily} />
      )}

      {/* Mode B: Scroll Reach Details & Milestone Curve */}
      {activeSubView === 'scroll' && (
        <div className="viewer-detail-card" data-testid="scroll-reach-card">
          <div className="detail-header-row">
            <div>
              <h4 className="detail-title">Scroll Reach Depth Distribution</h4>
              <p className="detail-sub">
                Vertical viewport penetration across visitor sessions for {selectedPage?.name || 'Selected Page'}.
              </p>
            </div>
            <div className="reach-kpi-badges">
              <div className="reach-kpi-pill">
                <span className="reach-kpi-label">Median Scroll Depth</span>
                <span className="reach-kpi-value" data-testid="median-scroll-depth">
                  {scrollReach?.medianScrollDepthPercent != null
                    ? `${scrollReach.medianScrollDepthPercent}%`
                    : 'N/A'}
                </span>
              </div>
              <div className="reach-kpi-pill">
                <span className="reach-kpi-label">Denominator</span>
                <span className="reach-kpi-value" data-testid="scroll-eligible-visits">
                  {scrollReach?.eligibleVisits != null
                    ? `${scrollReach.eligibleVisits.toLocaleString()} eligible visits`
                    : (scrollReach?.denominator || 'eligible_visits')}
                </span>
              </div>
            </div>
          </div>

          {scrollMilestones.length > 0 ? (
            <div className="milestones-list" data-testid="scroll-milestones-list">
              {scrollMilestones.map((item) => {
                const percentage = item.reachRate <= 1 ? item.reachRate * 100 : item.reachRate;
                const formattedRate = `${percentage.toFixed(1)}%`;
                return (
                  <div key={item.depthPercent} className="milestone-row" data-testid={`milestone-${item.depthPercent}`}>
                    <div className="milestone-label-col">
                      <span className="milestone-depth">{item.depthPercent}% Depth</span>
                    </div>
                    <div className="milestone-bar-col">
                      <div className="milestone-bar-track">
                        <div
                          className="milestone-bar-fill"
                          style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                        />
                      </div>
                    </div>
                    <div className="milestone-stat-col">
                      <span className="milestone-rate">{formattedRate}</span>
                      <span className="milestone-visits">
                        ({(item.reachedVisits ?? item.sessionCount)?.toLocaleString() || 0} visits)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="detail-empty-state">
              <FaArrowsAltV className="empty-icon" />
              <p>No scroll reach data available for this page in the selected period.</p>
            </div>
          )}
        </div>
      )}

      {/* Mode C: Element Hover Dwell Top 10 Ranking */}
      {activeSubView === 'hover' && (
        <div className="viewer-detail-card" data-testid="hover-dwell-card">
          <div className="detail-header-row">
            <div>
              <h4 className="detail-title">Element Hover Dwell Analysis</h4>
              <p className="detail-sub">
                Hover counts and dwell durations on interactive targets for {selectedPage?.name || 'Selected Page'}.
              </p>
            </div>
          </div>

          {/* Touch Exclusion Notice (Mandatory API requirement) */}
          <div className="notice-banner touch-notice" data-testid="touch-exclusion-banner" role="status">
            <FaInfoCircle className="notice-icon" />
            <div className="notice-text">
              <strong>Touch Devices Excluded: </strong>
              <span>
                Touch devices are excluded from hover calculations ({hoverDwell?.touchVisitsExcluded?.toLocaleString() || 0} touch visits excluded; {hoverDwell?.hoverCapableVisits?.toLocaleString() || 0} hover-capable visits evaluated). Touch hover is unavailable rather than zero.
              </span>
            </div>
          </div>

          {top10HoverElements.length > 0 ? (
            <div className="table-responsive">
              <h5 className="ranking-table-heading">Top 10 Most Hovered Elements</h5>
              <table className="elements-table" data-testid="hover-elements-table">
                <thead>
                  <tr>
                    <th>Element Target</th>
                    <th>Selector</th>
                    <th>Unique Visitors</th>
                    <th>Hover Count</th>
                    <th>Avg Dwell</th>
                    <th>Median Dwell</th>
                  </tr>
                </thead>
                <tbody>
                  {top10HoverElements.map((el, idx) => (
                    <tr key={el.selector || el.elementSelector || idx} data-testid={`hover-element-row-${idx}`}>
                      <td className="element-name-cell">
                        <strong>{el.targetName || el.selector || 'Interactive Element'}</strong>
                      </td>
                      <td className="selector-cell">
                        <code>{el.selector || el.elementSelector}</code>
                      </td>
                      <td>{(el.uniqueVisitors ?? el.hoverCount)?.toLocaleString() || 0}</td>
                      <td>{(el.hoverCount || 0).toLocaleString()}</td>
                      <td>{el.avgDwellMs != null ? `${(el.avgDwellMs / 1000).toFixed(2)}s` : '-'}</td>
                      <td>{el.medianDwellMs != null ? `${(el.medianDwellMs / 1000).toFixed(2)}s` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="detail-empty-state">
              <FaHandPointer className="empty-icon" />
              <p>No element hover dwell observations recorded for this page.</p>
            </div>
          )}
        </div>
      )}

      {/* Mode D: Section Attention Map Details & Exposure Bar Chart */}
      {activeSubView === 'attention' && (
        <div className="viewer-detail-card" data-testid="section-attention-card">
          <div className="detail-header-row">
            <div>
              <h4 className="detail-title">Section Attention & Exposure</h4>
              <p className="detail-sub">
                Screen viewport residence duration across sections for {selectedPage?.name || 'Selected Page'}.
              </p>
            </div>
          </div>

          {/* Measurement Basis Notice (Mandatory API requirement) */}
          <div className="notice-banner attention-notice" data-testid="attention-disclaimer-banner" role="note">
            <FaInfoCircle className="notice-icon" />
            <div className="notice-text">
              <strong>Measurement Basis ({attention?.attentionType || attention?.measurementBasis || 'screen_exposure_not_eye_tracking'}): </strong>
              <span>
                Measurements reflect viewport screen exposure duration, not biometric eye tracking.
              </span>
            </div>
          </div>

          {Array.isArray(attention?.sections) && attention.sections.length > 0 ? (
            <>
              {/* Bar chart showing seconds spent per page section */}
              <div className="attention-bar-chart-card" data-testid="attention-bar-chart">
                <h5 className="bar-chart-title">Seconds Spent per Page Section</h5>
                <div className="attention-bars-list">
                  {attention.sections.map((sec, idx) => {
                    const viewSecs = sec.avgViewTimeSeconds != null
                      ? sec.avgViewTimeSeconds
                      : ((sec.medianAttentionMs || 0) / 1000);
                    const maxSecs = Math.max(
                      ...attention.sections.map((s) => s.avgViewTimeSeconds ?? ((s.medianAttentionMs || 0) / 1000)),
                      1
                    );
                    const fillPct = Math.min(100, Math.round((viewSecs / maxSecs) * 100));
                    return (
                      <div key={sec.sectionId || idx} className="attention-bar-row">
                        <span className="attention-bar-label">Sec: {sec.sectionLabel || sec.sectionId}</span>
                        <div className="attention-bar-track">
                          <div className="attention-bar-fill" style={{ width: `${fillPct}%` }} />
                        </div>
                        <span className="attention-bar-val">{viewSecs.toFixed(1)} sec</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grid of section cards */}
              <div className="attention-grid" data-testid="attention-sections-grid">
                {attention.sections.map((section, idx) => (
                  <div key={section.sectionId || idx} className="attention-card" data-testid={`attention-section-${idx}`}>
                    <div className="attention-card-header">
                      <span className="section-name">{section.sectionLabel || section.sectionId}</span>
                      <span className="section-score-badge" data-testid={`attention-score-${idx}`}>
                        Score: {section.attentionScore != null ? section.attentionScore : 'N/A'}
                      </span>
                    </div>
                    <div className="attention-metric-row">
                      <div className="att-metric">
                        <span className="att-metric-label">{section.medianAttentionMs != null ? 'Median Attention' : 'Avg View Time'}</span>
                        <span className="att-metric-value">
                          {section.medianAttentionMs != null
                            ? `${(section.medianAttentionMs / 1000).toFixed(1)}s`
                            : (section.avgViewTimeSeconds != null ? `${section.avgViewTimeSeconds.toFixed(1)}s` : '-')}
                        </span>
                      </div>
                      {section.p90AttentionMs != null && (
                        <div className="att-metric">
                          <span className="att-metric-label">P90 Attention</span>
                          <span className="att-metric-value">
                            {(section.p90AttentionMs / 1000).toFixed(1)}s
                          </span>
                        </div>
                      )}
                      <div className="att-metric">
                        <span className="att-metric-label">{section.totalVisits != null ? 'Total Visits' : 'Total Impressions'}</span>
                        <span className="att-metric-value">
                          {(section.totalVisits ?? section.totalImpressions)?.toLocaleString() || 0}
                        </span>
                      </div>
                    </div>
                    {section.attentionScore != null && (
                      <div className="attention-meter-track">
                        <div
                          className="attention-meter-fill"
                          style={{ width: `${Math.min(100, Math.max(0, section.attentionScore))}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="detail-empty-state">
              <FaEye className="empty-icon" />
              <p>No section attention data recorded for this page.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

HeatmapViewerTab.propTypes = {
  summary: PropTypes.shape({
    totalInteractions: PropTypes.number,
    uniqueSessions: PropTypes.number,
    captureObserved: PropTypes.bool
  }),
  daily: PropTypes.array,
  density: PropTypes.array,
  scrollReach: PropTypes.shape({
    pageId: PropTypes.string,
    eligibleVisits: PropTypes.number,
    denominator: PropTypes.string,
    medianScrollDepthPercent: PropTypes.number,
    reachPoints: PropTypes.array,
    depthDistribution: PropTypes.array
  }),
  hoverDwell: PropTypes.shape({
    pageId: PropTypes.string,
    touchVisitsExcluded: PropTypes.number,
    hoverCapableVisits: PropTypes.number,
    elements: PropTypes.array
  }),
  attention: PropTypes.shape({
    pageId: PropTypes.string,
    measurementBasis: PropTypes.string,
    attentionType: PropTypes.string,
    sections: PropTypes.array
  }),
  pages: PropTypes.arrayOf(PropTypes.shape({
    pageId: PropTypes.string.isRequired,
    name: PropTypes.string,
    layoutVersion: PropTypes.number
  })),
  selectedPageId: PropTypes.string,
  selectedLayoutVersion: PropTypes.number,
  onPageChange: PropTypes.func,
  onLayoutVersionChange: PropTypes.func,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  organizationId: PropTypes.string,
  siteCollectionId: PropTypes.string,
  hasSessionReplayFeature: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  refreshNonce: PropTypes.number
};
